import { FC } from 'react';

import { useStatusToast } from '~/components/unified/hooks';
import { SettingsCardSkeleton } from '~/components/unified/SettingsCard';
import { UserRoleEnum } from '~/generated/graphql';

import { FormMemberAdd } from './FormMemberAdd';
import { TeamMembershipList } from './TeamMembershipList';
import { useAccountMembers } from './useAccountMembers';
import { useInviteCancel } from './useAccountMembers/useInviteCancel';
import { useInviteUser } from './useAccountMembers/useInviteUser';
import { useRemoveUser } from './useAccountMembers/useRemoveUser';
import { useUpdateRole } from './useAccountMembers/useUpdateRole';

export const TeamUserSettings: FC = () => {
  const { members, invites, loading, refetchMembers } = useAccountMembers();
  const { inviteUser } = useInviteUser();
  const { inviteCancel } = useInviteCancel();
  const { updateRole } = useUpdateRole();
  const { removeUser } = useRemoveUser();

  const { addSuccessToast, addErrorToast } = useStatusToast();

  const handleInviteUser = async (email: string) => {
    const result = await inviteUser({
      variables: {
        input: {
          email,
        },
      },
    });

    if (result.data?.inviteUser?.errors.length) {
      if (result.data.inviteUser.errors[0].field === 'base') {
        return addErrorToast({
          description: result.data.inviteUser.errors[0].messages[0],
          heading: 'Invite failed',
        });
      } else {
        return addErrorToast({
          description: `Unable to invite ${email}.`,
          heading: 'Invite failed',
        });
      }
    }

    addSuccessToast({
      description: `Invite sent to ${email}.`,
      heading: 'Invite sent',
    });

    refetchMembers();
  };

  const handlePermissionsChange = async (
    userId: string,
    role: UserRoleEnum,
  ) => {
    const result = await updateRole({
      variables: {
        input: {
          userId,
          role,
        },
      },
    });

    if (result.data?.updateUserRole?.errors.length) {
      return addErrorToast({
        heading: 'Permissions update failed',
        description: `Unable to update permissions.`,
      });
    }

    addSuccessToast({
      heading: 'Permissions updated',
      description: 'Permissions have been updated.',
    });

    refetchMembers();
  };

  const handleRemoveInvite = async (id: string) => {
    const result = await inviteCancel({
      variables: {
        input: {
          id,
        },
      },
    });

    if (result.data?.inviteCancel?.errors.length) {
      return addErrorToast({
        description: `Unable to cancel invite.`,
        heading: 'Invite cancel failed',
      });
    }

    addSuccessToast({
      heading: 'Invite cancelled',
    });

    refetchMembers();
  };

  const handleRemoveMember = async (userId: string) => {
    const result = await removeUser({
      variables: {
        input: {
          userId,
        },
      },
    });

    if (result.data?.destroyUser?.errors.length) {
      return addErrorToast({
        heading: 'Remove user failed',
        description: `Unable to remove user.`,
      });
    }

    addSuccessToast({
      heading: 'User removed',
      description: 'User has been removed.',
    });

    refetchMembers();
  };

  const LoadingState = () => {
    return (
      <div className="space-y-8">
        <SettingsCardSkeleton className="h-96" />
        <SettingsCardSkeleton className="h-48" />
      </div>
    );
  };

  return loading ? (
    <LoadingState />
  ) : (
    <div className="space-y-8">
      <FormMemberAdd onChange={handleInviteUser} />
      <TeamMembershipList
        invites={invites}
        memberships={members}
        onPermissionsChange={handlePermissionsChange}
        onRemoveInvite={handleRemoveInvite}
        onRemoveMember={handleRemoveMember}
      />
    </div>
  );
};
